import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';

import { insideNativeApp } from '../../auth/native-app';
import { isOwner } from '../../auth/roles';
import { useSession } from '../../auth/useSession';
import { planLabel } from '../../lib/plan';
import { setTema, useTema } from '../../lib/tema';
import { useBusiness } from '../../lib/useBusiness';
import { useActiveIndicator } from '../../lib/motion/hooks';
import { centerOf, withViewTransition } from '../../lib/motion/viewTransition';
import { folderLock, NavLabel } from './NavLabel';
import { folderForPath, type Folder, type NavModel } from './navModel';
import { TabIcon } from './TabIcon';
import { Sheet } from '../Sheet';

const PHONE_FOLDERS = 4;
/** What the phone keeps within a thumb: the day, the agenda, the clients and the counter. */
const PHONE_PRIORITY: readonly string[] = ['inicio', 'agenda', 'clientes', 'caja'];

function phoneOrder(folders: Folder[]): Folder[] {
  // A coach's own pages (coach-0, coach-1...) lead, in their order, ahead of the counter.
  const rank = (folder: Folder) => {
    if (folder.id.startsWith('coach-')) return -1;
    const index = PHONE_PRIORITY.indexOf(folder.id);
    return index === -1 ? PHONE_PRIORITY.length : index;
  };
  return [...folders].sort((a, b) => rank(a) - rank(b));
}

function FolderButton({ folder, active }: { folder: Folder; active: boolean }) {
  const first = folder.links[0]!;
  return (
    <Link to={first.to} className="hs-bottom-btn" data-active={active || undefined} aria-current={active ? 'page' : undefined}>
      <TabIcon name={first.to} />
      <span>
        <NavLabel label={folder.label} lock={folderLock(folder.links)} />
      </span>
    </Link>
  );
}

function MoreSheet({ open, model, rest, onClose }: { open: boolean; model: NavModel; rest: Folder[]; onClose: () => void }) {
  const { session, logout } = useSession();
  const business = useBusiness();
  const owner = isOwner(session?.role);
  const tema = useTema();
  return (
    <Sheet open={open} label="Más opciones" className="hs-more" onClose={onClose}>
      {rest.map((folder) => (
        <div key={folder.id} className="hs-more-group">
          {folder.links.length > 1 ? <p className="nav-group-label">{folder.label}</p> : null}
          {folder.links.map((link) => (
            <Link key={link.to} to={link.to} className="nav-item" onClick={onClose}>
              <NavLabel label={link.label} lock={link.lock} />
            </Link>
          ))}
        </div>
      ))}
      {model.settings.length > 0 ? (
        <>
          <p className="nav-group-label">Ajustes</p>
          {model.settings.map((link) => (
            <Link key={link.to} to={link.to} className="nav-item" onClick={onClose}>
              <NavLabel label={link.label} lock={link.lock} />
            </Link>
          ))}
        </>
      ) : null}
      <p className="nav-group-label">Tu cuenta</p>
      {owner && business && !insideNativeApp() ? (
        <Link to="/membresia" className="nav-item" onClick={onClose}>
          Tu plan <span className="account-menu-hint">{planLabel(business.plan ?? 'base')}</span>
        </Link>
      ) : null}
      <Link to="/configuracion" className="nav-item" onClick={onClose}>
        Apariencia
      </Link>
      <button type="button" className="nav-item" aria-pressed={tema === 'noche'} onClick={(event) => withViewTransition(() => setTema(tema === 'noche' ? 'dia' : 'noche'), centerOf(event.currentTarget))}>
        Tema <span className="account-menu-hint">{tema === 'noche' ? 'Noche' : 'Día'}</span>
      </button>
      <button type="button" className="nav-item sheet-logout" onClick={() => void logout()}>
        Salir
      </button>
    </Sheet>
  );
}

/** The phone's floating glass bar: four folders and Más, like the Helios CRM. */
export function PhoneBar({ model }: { model: NavModel }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [pathname]);
  const activeId = folderForPath(model, pathname)?.id;
  const ordered = phoneOrder(model.folders);
  const shown = ordered.slice(0, PHONE_FOLDERS);
  const rest = ordered.slice(PHONE_FOLDERS);
  const moreActive = !shown.some((folder) => folder.id === activeId);
  const bar = useRef<HTMLElement>(null);
  useActiveIndicator(bar, '.hs-bottom-btn[data-active]', moreActive ? 'more' : activeId);
  return (
    <>
      <nav className="hs-bottom hs-glass" aria-label="Carpetas" ref={bar} style={{ gridTemplateColumns: `repeat(${shown.length + 1}, minmax(0, 1fr))` }}>
        <span className="ind hs-bottom-thumb" aria-hidden="true" />
        {shown.map((folder) => (
          <FolderButton key={folder.id} folder={folder} active={folder.id === activeId} />
        ))}
        <button type="button" className="hs-bottom-btn" data-active={moreActive || undefined} aria-expanded={open} onClick={() => setOpen(true)}>
          <TabIcon name="more" />
          <span>Más</span>
        </button>
      </nav>
      <MoreSheet open={open} model={model} rest={rest} onClose={() => setOpen(false)} />
    </>
  );
}
